import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  addOrReplaceAndRunAnalysisStep,
  copyAnalysisViewSteps,
  deleteAnalysisSteps,
  updateAndRunAnalysisStep,
} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_SAMPLED_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES} from 'analysis-tool/AnalysisToolConstants';
import {isAnalysisOnlyLegacyChartStep} from 'analysis-tool/charting/AnalysisChartingUtils';
import {
  findAndAssertSingleStepDataFrameId,
  findAndAssertStepData,
  findAndAssertOutputStepData,
  findSingleStepOutputDataFrameId,
} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import getStepVisualizationConfigAndName from 'analysis-tool/data/getVisualizationConfigAndName';
import QueryingChartAndDataFrame from 'analysis-tool/ui/main-view/QueryingChartAndDataFrame';
import {assert} from 'Asserts';
import DataFrameWarningBanner from 'data-frames/graphql/DataFrameWarningBanner';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertIsAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {VISUALIZATION_TYPE_TO_FLEX_GROW} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {DeletePipelineStepsBehavior} from 'pipelines/PipelineDAO';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {getTableLineageAncestorSteps} from 'pipelines/StepLineageUtils';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import LoadingOverlay from 'ui-library/LoadingOverlay';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import {omit} from 'util/omitKey';
import {withLoadingScreen} from 'util/Promises';

import {getPersistedChartViewConfig} from '../charting/getPersistedVisualizationConfig';
import VisualizationBlock from '../charting/VisualizationBlock';
import StepLockedWarningBanner from '../components/StepLockedWarningBanner';
import FailedStepView from './FailedStepView';
import {getSaveToResultsOverflowOption} from './MainViewHelpers';
import {AnalysisMainViewItemType, MainViewComponentProps} from './MainViewTypes';

/**
 * Component to show a view created by a `CHART_VIEW` or `TABLE_VIEW` analysis step
 *
 * @package
 */
export default function ChartView(
  props: MainViewComponentProps<AnalysisMainViewItemType.CHART>
): JSX.Element | null {
  const {
    clientStateId,
    analysis,
    onChangeSelectedItem,
    onOpenStepInitializationView,
    step: chartOrTableStep,
  } = props;
  // TODO(BNCH-124884): Allow viewing box plots in the old chart view
  const viewSteps = getTableLineageAncestorSteps(analysis, chartOrTableStep);
  const inputStepData = findAndAssertStepData(chartOrTableStep, SINGLE_DATASET_INPUT_STEP_DATA_NAME, true);
  if (inputStepData.dataFrames.length === 0) {
    return chartOrTableStep.statusCode === PipelineStepStatusCode.FAILED ? (
      <FailedStepView
        {...props}
        selectedItem={{type: AnalysisMainViewItemType.FAILED, stepId: props.selectedItem.stepId}}
      />
    ) : (
      <CenteredHorizontalThrobber />
    );
  }

  const chartId =
    chartOrTableStep.statusCode === PipelineStepStatusCode.SUCCEEDED &&
    chartOrTableStep.definition.stepType === PipelineStepType.CHART_VIEW
      ? assertIsAccessible(
          findAndAssertOutputStepData(chartOrTableStep, SINGLE_CHART_OUTPUT_STEP_DATA_NAME).charts[0]
        ).id
      : undefined;

  const inputDataFrameId = findAndAssertSingleStepDataFrameId(
    chartOrTableStep,
    SINGLE_DATASET_INPUT_STEP_DATA_NAME,
    true
  );

  const sampledOutputDataFrameId =
    chartOrTableStep.statusCode === PipelineStepStatusCode.FAILED ||
    chartOrTableStep.definition.stepType === PipelineStepType.TABLE_VIEW
      ? null
      : findSingleStepOutputDataFrameId(chartOrTableStep, SINGLE_DATASET_SAMPLED_OUTPUT_STEP_DATA_NAME);

  const isAnalysisOnlyChartingType = isAnalysisOnlyLegacyChartStep(chartOrTableStep);

  const {visualizationConfig, viewName} = isAnalysisOnlyChartingType
    ? {
        visualizationConfig: null,
        viewName: getPipelineStepConfig(chartOrTableStep, PipelineStepType.CHART_VIEW, 0).chartName,
      }
    : getStepVisualizationConfigAndName(chartOrTableStep);

  const visualizationType = visualizationConfig ? getVisualizationType(visualizationConfig) : null;

  async function handleSaveChartName(newName: string): Promise<void> {
    if (newName === viewName) {
      return;
    }
    let stepConfig:
      | PipelineStepConfig<PipelineStepType.CHART_VIEW, 0>
      | PipelineStepConfig<PipelineStepType.TABLE_VIEW, 0>;
    if (visualizationConfig != null) {
      stepConfig =
        chartOrTableStep.definition.stepType === PipelineStepType.CHART_VIEW
          ? {
              chartConfig: getPersistedChartViewConfig(visualizationConfig),
              chartName: newName,
            }
          : {
              viewName: newName,
            };
    } else {
      const config = getPipelineStepConfig(chartOrTableStep, PipelineStepType.CHART_VIEW, 0);
      stepConfig = {
        chartName: newName,
        chartConfig: config.chartConfig,
      };
    }

    await withLoadingScreen(
      updateAndRunAnalysisStep(analysis.id, {
        stepId: chartOrTableStep.id,
        stepConfig,
      }),
      'Updating view...'
    );
  }

  const copyView = (): void => {
    withLoadingScreen(copyAnalysisViewSteps(analysis.id, viewSteps[0].id));
  };

  const deleteView = (): void => {
    runConfirmDeleteModal({typeName: 'view'}).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      return withLoadingScreen(
        deleteAnalysisSteps(
          analysis,
          {
            stepIds: [viewSteps[0].id],
            behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN,
          },
          clientStateId
        )
      );
    });
  };

  const promoteView = (): void => {
    withLoadingScreen(
      addOrReplaceAndRunAnalysisStep(analysis.id, {
        stepType: PipelineStepType.PROMOTE_DATASET,
        stepVersion: 0,
        stepConfig: {},
        stepInput: {
          inputDataFrameId: chartOrTableStep.outputStepDatas[0].dataFrames[0].id,
        },
        stepInputParents: {
          [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
            {
              stepId: chartOrTableStep.id,
              stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
            },
          ],
        },
      })
    );
  };
  return (
    <QueryingChartAndDataFrame
      chartId={chartId}
      inputDataFrameId={sampledOutputDataFrameId ?? inputDataFrameId}
      fullDataFrameId={inputDataFrameId}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({dataFrame, numRows, chart}) => {
        assert(!isInaccessible(dataFrame), 'User does not have access to step input');

        const downloadChart =
          chart != null
            ? async (): Promise<void> => {
                const signedURL = await performSignedFileQuery(
                  chart.rasterExternalFileId,
                  chart.rasterDownloadUrl,
                  chart.id
                );
                if (signedURL == null) {
                  return;
                }
                window.open(signedURL, '_blank', 'noopener noreferrer');
              }
            : undefined;

        return (
          <LoadingOverlay
            isLoading={
              !viewSteps.some((step) => step.statusCode === PipelineStepStatusCode.FAILED) &&
              viewSteps.some((step) =>
                [
                  PipelineStepStatusCode.BLOCKED,
                  PipelineStepStatusCode.WAITING,
                  PipelineStepStatusCode.RUNNING,
                ].includes(step.statusCode)
              )
            }
            isFaded={true}
          >
            <div
              className={css(
                visualizationStyles.wrapper,
                visualizationType &&
                  VISUALIZATION_TYPE_TO_FLEX_GROW[visualizationType] &&
                  visualizationStyles.fullHeight,
                isAnalysisOnlyChartingType && visualizationStyles.fullHeight
              )}
              {...getTestProps({component: 'ChartView', element: 'root'})}
            >
              <div className={css(visualizationStyles.banner)}>
                <StepLockedWarningBanner analysis={analysis} step={chartOrTableStep} />
              </div>
              <VisualizationBlock
                analysis={analysis}
                banner={
                  <>
                    <DataFrameWarningBanner
                      isRandomSubset={true}
                      name={viewName}
                      renderedColumns={dataFrame.dataFrameJson.columns.length}
                      renderedRows={dataFrame.dataFrameJson.numRows}
                      totalColumns={dataFrame.allColumnsJson.length}
                      totalRows={numRows}
                    />
                  </>
                }
                clientStateId={clientStateId}
                dataFrame={dataFrame.dataFrameJson}
                dataFrameId={dataFrame.id}
                viewSteps={viewSteps}
                chart={chart}
                renderFromChart={true}
                visualizationConfig={visualizationConfig}
                title={
                  analysis.permissions.writable ? (
                    <EditableTextField
                      value={viewName}
                      requestChange={handleSaveChartName}
                      dataTestAttrs={{component: 'EditableTextField', element: 'title'}}
                    />
                  ) : (
                    viewName
                  )
                }
                saveToResultsOverflowOption={
                  chartOrTableStep.outputStepDatas[0]?.dataFrames?.length > 0
                    ? getSaveToResultsOverflowOption(
                        analysis,
                        chartOrTableStep,
                        chartOrTableStep.outputStepDatas[0].dataFrames[0].id,
                        onChangeSelectedItem,
                        onOpenStepInitializationView
                      )
                    : null
                }
                onCopyClicked={copyView}
                onDeleteClicked={deleteView}
                onDownloadImageClicked={downloadChart}
                onPromoteClicked={promoteView}
                onEditClicked={(): void => {
                  onChangeSelectedItem({
                    type: AnalysisMainViewItemType.EDIT_CHART,
                    stepId: chartOrTableStep.id,
                  });
                }}
              />
            </div>
          </LoadingOverlay>
        );
      }}
    </QueryingChartAndDataFrame>
  );
}

export const visualizationStyles = StyleSheet.create({
  banner: {
    marginBottom: 10,
  },
  wrapper: {
    // There is a box shadow on PresentationalUniversalBlockView so no need to have it here as well,
    // otherwise the shadows overlap making it look like there's one extra-dark shadow.
    ...omit(SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES, 'boxShadow'),
  },
  fullHeight: {
    height: '100%',
  },
});
